import * as React from 'react';

import { headingVariants, type HeadingVariantProps } from './heading.variants';
import {
  INLINE_COPY_HOST_CLASS,
  InlineCopyBoundary,
} from '@/hen-ui/lib/inline-copy';
import { cn, resolveAnchorId } from '@/hen-ui/utils';
import { Slot } from '@radix-ui/react-slot';

export interface HeadingProps
  extends Omit<
      React.PropsWithChildren<React.HTMLAttributes<HTMLHeadingElement>>,
      'color'
    >,
    HeadingVariantProps {
  className?: string;
  asChild?: boolean;
  variant: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  /**
   * Rendered HTML element. Defaults to the element matching `variant`
   * (e.g. `variant="h3"` renders an `<h3>`). Set it to decouple the
   * semantic element from the visual style — for example keep the `h3`
   * look while rendering a `<p>` so the heading stays out of the page
   * outline. `asChild` still takes precedence when set.
   */
  tag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p';
  /**
   * Semantic heading color. `category-1` to `category-3` use the theme's
   * data category colors, the same ones the Linear Gauge segments use.
   */
  color?:
    | 'default'
    | 'white'
    | 'subtext'
    | 'primary'
    | 'secondary'
    | 'category-1'
    | 'category-2'
    | 'category-3';
  /** Text alignment. */
  align?: 'left' | 'center' | 'right';
  /**
   * Optional subtext rendered below the heading. When set, a `<p>` element
   * is rendered immediately after the heading with `b3` typography and
   * `subtext` color. Empty strings are treated as unset (no DOM emitted).
   */
  subtext?: string;
  /**
   * In-page jump target. Rendered as the heading element's `id`.
   * A leading `#` is ignored. Blank values are omitted.
   * When both this and `id` are set, this wins.
   */
  anchorId?: string;
}

/**
 * Heading component following HEN Design System standards.
 *
 * @example
 * // Basic usage
 * <Heading variant="h1">Heading H1</Heading>
 *
 * @example
 * // With HEN variant and size
 * <Heading variant="h1">
 *   Heading H1
 * </Heading>
 *
 * @example
 * // As a link using asChild
 * <Heading asChild variant="h1">
 *   <a href="/about">Heading H1</a>
 * </Heading>
 */
export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  (
    {
      className,
      variant = 'h1',
      tag,
      color,
      align,
      children,
      subtext,
      asChild = false,
      anchorId,
      id,
      'aria-describedby': consumerDescribedBy,
      ...props
    },
    ref,
  ) => {
    const anchor = resolveAnchorId(anchorId, id);
    const Comp: React.ElementType = asChild ? Slot : (tag ?? variant);
    const trimmedSubtext = subtext?.trim() || '';
    const hasSubtext = !!trimmedSubtext;

    const subtextId = React.useId();

    // Merge consumer's aria-describedby with subtext id instead of overwriting
    const mergedDescribedBy = hasSubtext
      ? [consumerDescribedBy, subtextId].filter(Boolean).join(' ')
      : consumerDescribedBy;

    const headingEl = (
      <InlineCopyBoundary>
        <Comp
          className={cn(
            INLINE_COPY_HOST_CLASS,
            headingVariants({ variant, color, align }),
            className,
          )}
          ref={ref}
          {...props}
          {...(anchor ? { id: anchor } : {})}
          {...(mergedDescribedBy
            ? { 'aria-describedby': mergedDescribedBy }
            : {})}
        >
          {children}
        </Comp>
      </InlineCopyBoundary>
    );

    if (!hasSubtext) return headingEl;

    return (
      <>
        {headingEl}
        <p
          id={subtextId}
          className={cn(
            'font-body text-b3 leading-b3 text-subtext',
            align === 'center' && 'text-center',
            align === 'right' && 'text-right',
            align === 'left' && 'text-left',
          )}
        >
          {trimmedSubtext}
        </p>
      </>
    );
  },
);

Heading.displayName = 'Heading';
